import { createContext, useContext, useEffect, useRef, useState } from "react";
import ContextPanel from "./ContextPanel.jsx";
import { loadFindingSession } from "../lib/findingSession.js";
import { query } from "../lib/dataClient.js";
import { createSessionTabReader } from "../lib/sessionTabReader.js";
import { writeUrlParam } from "../lib/urlParams.js";
import { makeTraceTab } from "../lib/traceTabs.js";
import "./DiscoverySessionWorkspace.css";

const SessionWorkspaceContext = createContext(null);
const EMPTY_SCOPE = [];
export const useDiscoverySession = () => useContext(SessionWorkspaceContext);

export function findingCitationMessages(detail) {
    if (detail?.citations) return [...new Set(detail.citations.map((c) => c.source?.messageId).filter(Boolean))];
    const array = (value) => { try { return Array.isArray(value) ? value : JSON.parse(value || "[]"); } catch { return []; } };
    const messages = array(detail?.citation_message_ids), quotes = array(detail?.citation_quotes);
    if (!Array.isArray(messages) || !Array.isArray(quotes) || messages.length !== quotes.length) return [];
    return [...new Set(messages.filter((id) => typeof id === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id) && !/^0{8}-0{4}-0{4}-0{4}-0{12}$/.test(id)))];
}

export { loadFindingSession } from "../lib/findingSession.js";

function SessionReader({ selection, onClose }) {
    const [readingView, setReadingView] = useState("lanes");
    const [address, setAddress] = useState(selection.member.trace_id);
    const [attempt, setAttempt] = useState(0);
    const [tabs, setTabs] = useState([]);
    const [error, setError] = useState(null);
    const [pending, setPending] = useState(true);
    const [focusRequest, setFocusRequest] = useState(null);
    const current = useRef(tabs);
    current.current = tabs;
    const reader = useRef(null);
    const titleRef = useRef(null);
    useEffect(() => { titleRef.current?.focus(); }, []);
    useEffect(() => {
        let live = true;
        setPending(true); setError(null); setTabs([]); setFocusRequest(null);
        reader.current = createSessionTabReader({ query, tabs: () => current.current,
            setTabs: (update) => { if (live) setTabs(update); }, warn: () => {} });
        loadFindingSession(address, (method, params) => {
            if (!live) return Promise.reject(new Error("Session workspace closed."));
            return query(method, params);
        }).then((trace) => {
            if (live) { setTabs([makeTraceTab(address, trace)]); setPending(false); }
        }).catch(() => {
            if (live) { setError("Could not load this session. Retry, or return to the finding."); setPending(false); }
        });
        return () => { live = false; reader.current = null; };
    }, [address, attempt]);
    const trace = tabs[0]?.data;
    const citationIds = findingCitationMessages(selection.detail);
    const matches = (trace?.turns || []).flatMap((turn, index) => citationIds.includes(turn.messageId) ? [index] : []);
    // A quote's "Show in session" lands on that quote's message once loaded.
    const focusMessageId = selection.focusMessageId ?? null;
    useEffect(() => {
        if (!trace || !focusMessageId) return;
        const turn = (trace.turns || []).findIndex((t) => t.messageId === focusMessageId);
        if (turn >= 0) setFocusRequest({ turn, arrival: Date.now() });
    }, [trace, focusMessageId]);
    const originSession = address === selection.member.trace_id;
    const openTrace = (id) => { if (typeof id === "string" && id) { writeUrlParam("turn", null); setReadingView("lanes"); setAddress(id); } };
    return <section className="discovery-session-workspace" aria-label="Session workspace" aria-busy={pending} onKeyDown={(event) => {
        if (event.key !== "Escape" || event.defaultPrevented) return;
        const dockClose = event.currentTarget.querySelector('[aria-label="Close detail dock"]');
        event.preventDefault(); event.stopPropagation();
        if (dockClose) dockClose.click(); else onClose();
    }}>
        <header className="discovery-session-origin">
            <button onClick={onClose}>Back to finding</button>
            <div><h2 ref={titleRef} tabIndex={-1} title={selection.detail.title}>{selection.detail.title || "Finding session"}</h2>
                <p>{selection.scopeLabel || "Discovery selection preserved"} / Full captured session{trace?.endpointName ? ` / ${trace.endpointName}` : ""}</p></div>
            {!originSession && <button onClick={() => openTrace(selection.member.trace_id)}>Return to finding's session</button>}
            {originSession && <button disabled={!matches.length} onClick={() => setFocusRequest({ turn: matches[0], arrival: Date.now() })}>Jump to finding</button>}
        </header>
        <div className="discovery-session-location" role="status">
            {originSession && !pending && trace && (matches.length
                ? `${matches.length} cited messages located in the loaded portion. The session may include activity after this review.`
                : citationIds.length ? (trace.record?.exhausted ? "The cited messages were not found in the retained session." : "The cited messages are not in the loaded portion. Load earlier messages to locate them.")
                    : "This finding has no message locator. Its stored quotes remain in the finding preview.")}
        </div>
        {pending && <div className="discovery-session-status"><p role="status">Loading captured session...</p><button onClick={onClose}>Cancel and return</button></div>}
        {error && <div className="discovery-session-status"><p role="alert">{error}</p><button onClick={() => setAttempt((n) => n + 1)}>Retry session</button></div>}
        {trace && <div className="discovery-session-reader"><ContextPanel selectedTrace={trace} activeTab="request" hideHeader
            scopePath={EMPTY_SCOPE} baseContextId="discovery-session" turnFocusRequest={focusRequest}
            readingView={readingView} onReadingViewChange={setReadingView}
            onOpenTrace={openTrace} onLoadEarlier={() => reader.current?.readPage(address)} /></div>}
    </section>;
}

export default function DiscoverySessionWorkspace({ children, navigation }) {
    const [selection, setSelection] = useState(null);
    const origin = useRef(null);
    const active = useRef(null);
    const returnFocus = useRef(null);
    const pendingNavigation = useRef(null);
    const restore = () => {
        active.current = null; setSelection(null);
        const action = pendingNavigation.current;
        pendingNavigation.current = null;
        requestAnimationFrame(() => {
            if (action) action();
            else if (returnFocus.current?.isConnected) returnFocus.current.focus({ preventScroll: true });
        });
    };
    useEffect(() => {
        const pop = () => {
            if (active.current && window.history.state?.discoverySessionWorkspace !== active.current) restore();
        };
        window.addEventListener("popstate", pop);
        return () => {
            window.removeEventListener("popstate", pop);
            if (active.current && window.history.state?.discoverySessionWorkspace === active.current && origin.current) {
                window.history.replaceState(origin.current.state, "", origin.current.url);
            }
        };
    }, []);
    const open = (next) => {
        returnFocus.current = document.activeElement;
        origin.current = { url: window.location.href, state: window.history.state };
        active.current = crypto.randomUUID();
        const url = new URL(window.location.href);
        url.searchParams.set("hist", "lanes"); url.searchParams.delete("turn");
        window.history.pushState({ ...window.history.state, discoverySessionWorkspace: active.current }, "", url);
        setSelection(next);
    };
    const navigate = (action) => {
        if (!active.current) { action(); return; }
        pendingNavigation.current = action;
        window.history.back();
    };
    return <SessionWorkspaceContext.Provider value={open}>
        {navigation?.(navigate, Boolean(selection))}
        <div className="discovery-session-host">
            <div className="discovery-session-underlay" hidden={Boolean(selection)}>{children}</div>
            {selection && <SessionReader selection={selection} onClose={() => window.history.back()} />}
        </div>
    </SessionWorkspaceContext.Provider>;
}
